import { Heart, Car, ArrowRight, Camera, Fuel, Gauge, Settings2 } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAppData } from '@/contexts/AppDataContext';
import { useTheme } from '@/contexts/ThemeContext';
import { layout4ThemeStyle } from './theme';
import { secureGetItem, secureSetItem } from '@/lib/storage';
import { generateVehicleSlug } from '@/lib/slug';
import { useStore } from '@/contexts/StoreContext';
import { generateResponsiveImageUrls, getImageLoadingStrategy } from '@/lib/imageOptimization';
import './styles.css';

interface Vehicle {
  id: number;
  brand: string;
  model: string;
  version: string;
  price: number;
  photos: string[];
  mileage: number;
  fuel: string;
  manufactureYear: number;
  modelYear: number;
  color: string;
  transmission: string;
  isZeroKm: boolean;
  store?: string;
  type?: number;
  changed?: {
    featured?: boolean;
  };
  statistics?: {
    views?: number;
    favorites?: number;
  };
  optionals?: string[];
}

interface VehicleCardProps {
  vehicle: Vehicle;
  whatsappUrl?: string;
  featured?: boolean;
}

export const VehicleCard = ({ vehicle }: VehicleCardProps) => {
  const { config } = useAppData();
  const { colors } = useTheme();
  const themeStyle = layout4ThemeStyle(colors);
  const { availableStores, selectedStore } = useStore();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFavorited, setIsFavorited] = useState(false);
  const [vehicleSlug, setVehicleSlug] = useState<string>(() => {
    const brand = (vehicle.brand || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-');
    const model = (vehicle.model || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-');
    return `carro/${brand}-${model}-${vehicle.modelYear || vehicle.id}-${vehicle.id}`;
  });
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(true);

  const buttonColor = colors?.button || '#D01818';
  const secondaryColor = colors?.secondary || '#64748b';

  const storeId = vehicle.store;
  const vehicleStore = useMemo(() => {
    if (!config?.stores) return null;
    return storeId
      ? config.stores.find((s: any) => s._id === storeId)
      : (selectedStore || config.stores[0]);
  }, [config?.stores, storeId, selectedStore]);
  const showStoreBadge = availableStores.length > 1 && vehicleStore && storeId;
  const isFeatured = !!vehicle.changed?.featured;

  useEffect(() => {
    const init = async () => {
      try {
        secureGetItem('favorites').then(favorites => {
          if (favorites && Array.isArray(favorites)) {
            setIsFavorited(favorites.includes(vehicle.id));
          }
        }).catch(() => {});

        generateVehicleSlug(vehicle).then(slug => {
          setVehicleSlug(slug);
        }).catch(() => {});
      } catch {
        /* card still renders */
      }
    };
    init();
  }, [vehicle.id]);

  const toggleFavorite = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const favorites = await secureGetItem('favorites') || [];
    let updatedFavorites = Array.isArray(favorites) ? [...favorites] : [];

    if (updatedFavorites.includes(vehicle.id)) {
      updatedFavorites = updatedFavorites.filter((id: number) => id !== vehicle.id);
      setIsFavorited(false);
    } else {
      updatedFavorites.push(vehicle.id);
      setIsFavorited(true);
    }

    await secureSetItem('favorites', updatedFavorites);
    window.dispatchEvent(new Event('favoritesUpdated'));
  };

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);

  const formatMileage = (mileage: number) => {
    if (mileage === 0) return 'Zero KM';
    return new Intl.NumberFormat('pt-BR').format(mileage) + ' km';
  };

  const handleImageLoad = () => {
    setIsImageLoading(false);
    setImageLoaded(true);
  };

  useEffect(() => {
    if (vehicle.photos?.[currentImageIndex] && currentImageIndex !== 0) {
      setIsImageLoading(true);
      setImageLoaded(false);
    }
  }, [currentImageIndex, vehicle.photos]);

  const handleCardClick = (e: React.MouseEvent) => {
    if (isImageLoading) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const photoCount = vehicle.photos?.length || 0;

  return (
    <article
      className="wl4-vehicle-card group h-full flex flex-col"
      style={themeStyle}
    >
      <Link
        to={`/estoque/${vehicleSlug}`}
        className="wl4-vehicle-card-link flex flex-col flex-1 min-h-0"
        onClick={handleCardClick}
      >
        <div className="wl4-vehicle-media">
          {isImageLoading && !imageLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-[#EFEFEF] z-20">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--wl4-border)] border-t-[var(--wl4-accent)]" />
            </div>
          )}

          {vehicle.photos?.[currentImageIndex] ? (
            (() => {
              const imageUrl = vehicle.photos[currentImageIndex];
              const optimized = generateResponsiveImageUrls(imageUrl, 340, 212);
              const loadingStrategy = getImageLoadingStrategy(currentImageIndex === 0, currentImageIndex === 0);
              return (
                <img
                  src={optimized.src}
                  srcSet={optimized.srcSet}
                  sizes={optimized.sizes || '(max-width: 640px) 90vw, 320px'}
                  alt={`${vehicle.brand} ${vehicle.model}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                  loading={loadingStrategy.loading}
                  fetchpriority={loadingStrategy.fetchpriority}
                  decoding="async"
                  width={optimized.width}
                  height={optimized.height}
                  onLoad={handleImageLoad}
                  onError={(e) => { e.currentTarget.style.display = 'none'; setIsImageLoading(false); setImageLoaded(true); }}
                />
              );
            })()
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[var(--wl4-muted)]">
              <Car className="h-10 w-10 opacity-40" />
            </div>
          )}

          <div className="wl4-vehicle-badges-top">
            {isFeatured && (
              <span className="wl4-vehicle-badge wl4-vehicle-badge--featured">Destaque</span>
            )}
            {vehicle.isZeroKm && (
              <span className="wl4-vehicle-badge wl4-vehicle-badge--featured">Zero KM</span>
            )}
            {showStoreBadge && (
              <span className="wl4-vehicle-badge wl4-vehicle-badge--store" style={{ backgroundColor: secondaryColor }}>
                {vehicleStore?.company}
              </span>
            )}
          </div>

          {photoCount > 0 && (
            <div className="wl4-vehicle-photos-count">
              <Camera className="h-3.5 w-3.5" />
              <span>{photoCount}</span>
            </div>
          )}

          {vehicle.modelYear && (
            <span className="wl4-vehicle-badge wl4-vehicle-badge--year">
              {vehicle.modelYear}
            </span>
          )}

          {photoCount > 1 && (
            <div className="wl4-vehicle-photo-bar" aria-hidden="true">
              {vehicle.photos.map((_, index) => (
                <span
                  key={index}
                  className={`wl4-vehicle-photo-segment ${index === currentImageIndex ? 'is-active' : ''}`}
                />
              ))}
            </div>
          )}
        </div>

        <div className="wl4-vehicle-body">
          <p className="wl4-vehicle-eyebrow">{vehicle.brand}</p>
          <h3 className="wl4-vehicle-title">
            {vehicle.model} {vehicle.modelYear}
          </h3>
          {vehicle.version && (
            <p className="wl4-vehicle-version line-clamp-1">{vehicle.version}</p>
          )}
          <p className="wl4-vehicle-price">{formatPrice(vehicle.price)}</p>
        </div>

        <div className="wl4-vehicle-specs">
          <div className="wl4-vehicle-spec">
            <Fuel className="wl4-vehicle-spec-icon" />
            <span className="wl4-vehicle-spec-label">Combustível</span>
            <span className="wl4-vehicle-spec-value">{vehicle.fuel || '—'}</span>
          </div>
          <div className="wl4-vehicle-spec">
            <Gauge className="wl4-vehicle-spec-icon" />
            <span className="wl4-vehicle-spec-label">Quilometragem</span>
            <span className="wl4-vehicle-spec-value">
              {vehicle.mileage !== undefined && vehicle.mileage !== null ? formatMileage(vehicle.mileage) : '—'}
            </span>
          </div>
          <div className="wl4-vehicle-spec">
            <Settings2 className="wl4-vehicle-spec-icon" />
            <span className="wl4-vehicle-spec-label">Câmbio</span>
            <span className="wl4-vehicle-spec-value">{vehicle.transmission || '—'}</span>
          </div>
        </div>

        <div className="wl4-vehicle-footer">
          <span className="wl4-vehicle-cta">
            Ver detalhes
            <ArrowRight className="h-4 w-4" />
          </span>
          <button
            type="button"
            onClick={toggleFavorite}
            className="wl4-vehicle-favorite"
            title={isFavorited ? 'Remover dos favoritos' : 'Favoritar'}
            aria-label={isFavorited ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
          >
            <Heart className={`h-4 w-4 ${isFavorited ? 'fill-[var(--wl4-accent)] text-[var(--wl4-accent)]' : ''}`} />
          </button>
        </div>
      </Link>
    </article>
  );
};
